import {
  Shield,
  CheckCircle,
  Type,
  Calendar,
  ArrowRightLeft,
  Code,
  FileText,
  Hash,
  Key,
  QrCode,
  CreditCard,
  Mail,
  Phone,
  Globe,
  Clock,
  Binary,
  Braces,
  ImageIcon,
  Settings,
  Lock,
  Banknote,
  Search,
  LinkIcon,
  FileCheck,
  Fingerprint,
  Database,
  IdCard,
  MapPin,
  GitBranch,
  CalendarCheck,
  CalendarMinus,
  Ruler,
} from "lucide-react"

export const categories = [
  {
    id: "encryption",
    name: "Encryption & Hash",
    icon: Shield,
    description: "Encryption, hash and security tools",
  },
  {
    id: "validators",
    name: "Validators",
    icon: CheckCircle,
    description: "Document and format validation",
  },
  {
    id: "text",
    name: "Text",
    icon: Type,
    description: "Text processing and analysis",
  },
  {
    id: "date",
    name: "Date & Time",
    icon: Calendar,
    description: "Date and time calculations",
  },
  {
    id: "conversion",
    name: "Conversion",
    icon: ArrowRightLeft,
    description: "Unit and format converters",
  },
  {
    id: "development",
    name: "Development",
    icon: Code,
    description: "Developer tools",
  },
  {
    id: "files",
    name: "Files",
    icon: FileText,
    description: "File analysis and processing",
  },
]

export const tools = [
  {
    id: "hash-generator",
    name: "Hash Generator",
    description: "Generate SHA1, SHA256, SHA512, MD5 hashes",
    icon: Hash,
    category: "encryption",
    tags: ["hash", "sha", "md5", "security"],
  },
  {
    id: "base64-encoder",
    name: "Base64 Encoder",
    description: "Encode and decode text in Base64",
    icon: Binary,
    category: "encryption",
    tags: ["base64", "encoding", "text"],
  },
  {
    id: "caesar-cipher",
    name: "Caesar Cipher",
    description: "Encrypt and decrypt text using Caesar cipher",
    icon: Lock,
    category: "encryption",
    tags: ["caesar", "cipher", "classic"],
  },
  {
    id: "password-generator",
    name: "Password Generator",
    description: "Generate secure customizable passwords",
    icon: Key,
    category: "encryption",
    tags: ["password", "security", "random"],
  },
  {
    id: "qr-generator",
    name: "QR Generator",
    description: "Generate QR codes from text or URLs",
    icon: QrCode,
    category: "encryption",
    tags: ["qr", "code", "url"],
  },
  {
    id: "dni-validator",
    name: "DNI/NIE Validator",
    description: "Validate Spanish identity documents",
    icon: IdCard,
    category: "validators",
    tags: ["dni", "nie", "spain", "document"],
  },
  {
    id: "card-validator",
    name: "Card Validator",
    description: "Validate credit card numbers",
    icon: CreditCard,
    category: "validators",
    tags: ["card", "luhn", "credit"],
  },
  {
    id: "email-validator",
    name: "Email Validator",
    description: "Validate email address format",
    icon: Mail,
    category: "validators",
    tags: ["email", "mail", "format"],
  },
  {
    id: "url-validator",
    name: "URL Validator",
    description: "Validate URL format",
    icon: Globe,
    category: "validators",
    tags: ["url", "web", "format"],
  },
  {
    id: "phone-validator",
    name: "Phone Validator",
    description: "Validate international phone numbers",
    icon: Phone,
    category: "validators",
    tags: ["phone", "international", "format"],
  },
  {
    id: "iban-validator",
    name: "IBAN Validator",
    description: "Validate IBAN banking codes",
    icon: Banknote,
    category: "validators",
    tags: ["iban", "bank", "account"],
  },
  {
    id: "text-counter",
    name: "Text Counter",
    description: "Count characters, words and lines",
    icon: Hash,
    category: "text",
    tags: ["counter", "characters", "words"],
  },
  {
    id: "case-converter",
    name: "Case Converter",
    description: "Convert text between different cases",
    icon: Type,
    category: "text",
    tags: ["uppercase", "lowercase", "camelcase"],
  },
  {
    id: "lorem-generator",
    name: "Lorem Ipsum Generator",
    description: "Generate Lorem Ipsum placeholder text",
    icon: FileText,
    category: "text",
    tags: ["lorem", "ipsum", "placeholder"],
  },
  {
    id: "text-diff",
    name: "Text Comparator",
    description: "Compare differences between two texts",
    icon: GitBranch,
    category: "text",
    tags: ["differences", "comparison", "text"],
  },
  {
    id: "text-extractor",
    name: "Text Extractor",
    description: "Extract emails and URLs from text",
    icon: Search,
    category: "text",
    tags: ["extraction", "email", "url"],
  },
  {
    id: "slug-generator",
    name: "Slug Generator",
    description: "Generate URL-friendly slugs from text",
    icon: LinkIcon,
    category: "text",
    tags: ["slug", "url", "friendly"],
  },
  {
    id: "age-calculator",
    name: "Age Calculator",
    description: "Calculate exact age in years, months and days",
    icon: Calendar,
    category: "date",
    tags: ["age", "years", "birthday"],
  },
  {
    id: "date-diff",
    name: "Date Difference",
    description: "Calculate differences between two dates",
    icon: CalendarMinus,
    category: "date",
    tags: ["difference", "dates", "time"],
  },
  {
    id: "timestamp-converter",
    name: "Timestamp Converter",
    description: "Convert between timestamps and dates",
    icon: Clock,
    category: "date",
    tags: ["timestamp", "unix", "date"],
  },
  {
    id: "working-days",
    name: "Working Days",
    description: "Calculate working days between dates",
    icon: CalendarCheck,
    category: "date",
    tags: ["workdays", "work", "calendar"],
  },
  {
    id: "unit-converter",
    name: "Unit Converter",
    description: "Convert between different measurement units",
    icon: Ruler,
    category: "conversion",
    tags: ["units", "measurement", "conversion"],
  },
  {
    id: "number-base",
    name: "Number Base Converter",
    description: "Convert numbers between different bases",
    icon: Binary,
    category: "conversion",
    tags: ["binary", "hexadecimal", "octal"],
  },
  {
    id: "coordinate-converter",
    name: "Coordinate Converter",
    description: "Convert DMS coordinates to decimal and vice versa",
    icon: MapPin,
    category: "conversion",
    tags: ["coordinates", "gps", "location"],
  },
  {
    id: "json-formatter",
    name: "JSON Formatter",
    description: "Format, validate and minify JSON",
    icon: Braces,
    category: "development",
    tags: ["json", "format", "validation"],
  },
  {
    id: "uuid-generator",
    name: "UUID Generator",
    description: "Generate unique UUID identifiers",
    icon: Settings,
    category: "development",
    tags: ["uuid", "identifier", "unique"],
  },
  {
    id: "regex-helper",
    name: "Regex Helper",
    description: "Test and validate regular expressions",
    icon: Search,
    category: "development",
    tags: ["regex", "pattern", "validation", "test"],
  },
  {
    id: "fake-data-generator",
    name: "Fake Data Generator",
    description: "Generate fake data for testing",
    icon: Database,
    category: "development",
    tags: ["data", "fake", "testing"],
  },
  {
    id: "file-analyzer",
    name: "File Analyzer",
    description: "Analyze file information",
    icon: FileCheck,
    category: "files",
    tags: ["file", "analysis", "information"],
  },
  {
    id: "image-compressor",
    name: "Image Compressor",
    description: "Compress images while maintaining quality",
    icon: ImageIcon,
    category: "files",
    tags: ["image", "compression", "optimization"],
  },
  {
    id: "file-hash",
    name: "File Hash",
    description: "Calculate file hashes",
    icon: Fingerprint,
    category: "files",
    tags: ["hash", "file", "verification"],
  },
]
